import { matchSorter } from 'match-sorter'
import {
  HeadlessTableRow,
  HeadlessTableConfig,
  HeadlessTableDefaultState,
  HeadlessTableServerState,
  HeadlessTableState,
  HeadlessTableParams
} from './types'

export function getParamName(prefix: string | undefined, name: 'sort' | 'limit' | 'skip' | 'search' | (string & {})) {
  return prefix ? `${prefix}-${name}` : name
}

export function getTableStateParams<C extends HeadlessTableConfig<any, any, any, any>>({
  config,
  searchParams,
  defaultState,
  clientQuickFilters
}: {
  config: C
  searchParams: URLSearchParams
  defaultState?: HeadlessTableDefaultState<C>
  clientQuickFilters?: string[]
}): HeadlessTableParams<C> {
  function getParam(key: string) {
    const value = searchParams.get(getParamName(config.prefix, key))
    if (value) {
      try {
        return JSON.parse(decodeURIComponent(value))
      } catch {
        console.log(`Could not decode ${value} for "${key}"`)
        return null
      }
    } else {
      return null
    }
  }

  const filterKeys = Object.keys(config.filters)
  const filters = Object.fromEntries(
    filterKeys.map((key) => {
      const paramName = getParamName(config.prefix, key)
      if (searchParams.has(paramName)) {
        const value = searchParams.get(paramName)
        const decoded = value ? JSON.parse(decodeURIComponent(value)) : null
        return [key, decoded]
      } else {
        const filterDefaults = defaultState?.filters || {}
        return [key, filterDefaults[key] === undefined ? null : filterDefaults[key]]
      }
    })
  ) as HeadlessTableParams<C>['filters']

  const invalidMostRelevant = getParam('sort') === 'mostRelevant' && !searchParams.get(getParamName(config.prefix, 'search'))

  const sort =
    searchParams.has(getParamName(config.prefix, 'sort')) && !invalidMostRelevant
      ? getParam('sort')
      : defaultState?.sort || Object.keys(config.sorts)[0] || ''
  const limit = getParam('limit') || defaultState?.limit || 25
  // convert skip to number
  const skipRaw = Number(getParam('skip'))
  const skip = !isNaN(skipRaw) ? Math.max(0, skipRaw) : 0
  const quickFilter = searchParams.has(getParamName(config.prefix, 'quickFilter'))
    ? getParam('quickFilter')
    : defaultState?.quickFilter || null

  const search = searchParams.get(getParamName(config.prefix, 'search')) || null

  if (clientQuickFilters?.includes(quickFilter)) {
    return { quickFilter, limit: null, skip: 0, search: null, filters: {}, sort: null }
  } else {
    return { filters, sort, limit, skip, search, quickFilter }
  }
}

export function applyTableStateParams<C extends HeadlessTableConfig<any, any, any, any>>({
  config,
  rows,
  params
}: {
  config: C
  rows: C extends HeadlessTableConfig<infer R, any, any, any> ? R[] : HeadlessTableRow[]
  params: HeadlessTableParams<C>
}): { rows: C extends HeadlessTableConfig<infer R, any, any, any> ? R[] : HeadlessTableRow[]; rowCount: number } {
  const filterKeys = Object.keys(params.filters || {}).filter(
    (f) => (params.filters || {})[f] !== null && (params.filters || {})[f] !== undefined
  )
  const filteredRows = rows
    .filter((r) => filterKeys.every((f) => (config.filters[f] as any)(r, (params.filters || {})[f], f)))
    .filter(
      (r) =>
        !params.quickFilter ||
        !config.quickFilters ||
        (config.quickFilters[params.quickFilter] as any)(r, params.quickFilter)
    )
  const terms = params.search?.split(' ')
  const searchedRows = terms?.length
    ? terms.reduceRight((results, term) => matchSorter(results, term, config.searchOptions), filteredRows)
    : filteredRows

  if (params.sort && params.sort !== 'mostRelevant') {
    searchedRows.sort((a, b) => {
      return (config.sorts[params.sort] as any)(a, b, params.sort)
    })
  }
  const limitedRows = searchedRows.slice(
    params.skip,
    params.limit ? params.skip + params.limit : undefined
  ) as HeadlessTableState<C>['rows']
  return { rows: limitedRows, rowCount: searchedRows.length }
}

type getTableStateParams1<C extends HeadlessTableConfig<any, any, any, any>> = {
  config: C
  rows: C extends HeadlessTableConfig<infer R, any, any, any> ? R[] : HeadlessTableRow[]
  searchParams: URLSearchParams
  defaultState?: HeadlessTableDefaultState<C>
  autoFilter?: boolean
  /** if one of these quick filters is applied, filtering will be done on the client */
  clientQuickFilters?: (keyof C['quickFilters'])[]
}

type getTableStateParams2<C extends HeadlessTableConfig<any, any, any, any>> = {
  config: C
  searchParams: URLSearchParams
  defaultState?: HeadlessTableDefaultState<C>
  /** if one of these quick filters is applied, filtering will be done on the client */
  clientQuickFilters?: (keyof C['quickFilters'])[]
}
export function getTableState<C extends HeadlessTableConfig<any, any, any, any>>(
  params: getTableStateParams1<C>
): HeadlessTableServerState<C>
export function getTableState<C extends HeadlessTableConfig<any, any, any, any>>(
  params: getTableStateParams2<C>
): Omit<HeadlessTableServerState<C>, 'rows' | 'rowCount'>

export function getTableState<C extends HeadlessTableConfig<any, any, any, any>>({
  rows,
  config,
  searchParams,
  defaultState,
  autoFilter = false,
  clientQuickFilters
}: {
  config: C
  rows?: C extends HeadlessTableConfig<infer R, any, any, any> ? R[] : HeadlessTableRow[]
  searchParams: URLSearchParams
  defaultState?: HeadlessTableDefaultState<C>
  autoFilter?: boolean
  /** if one of these quick filters is applied, filtering will be done on the client */
  clientQuickFilters?: (keyof C['quickFilters'])[]
}) {
  const defaultSort = Object.keys(config.sorts)[0]
  defaultState = defaultState ? { sort: defaultSort, ...defaultState } : { sort: defaultSort }
  const params = getTableStateParams({ config, searchParams, defaultState })
  if (autoFilter && clientQuickFilters && params.quickFilter && clientQuickFilters.includes(params.quickFilter)) {
    if (!rows) {
      throw new Error('Must supply rows if autoFilter is true')
    }
    const limitedRows = rows.filter(
      (r) =>
        !params.quickFilter ||
        !config.quickFilters ||
        (config.quickFilters[params.quickFilter] as any)(r, params.quickFilter)
    ) as any
    return {
      serverParams: params,
      defaultState: defaultState,
      rows: limitedRows,
      filteringTier: 'server',
      clientQuickFilters,
      rowCount: limitedRows.length
    }
  } else if (autoFilter) {
    if (!rows) {
      throw new Error('Must supply rows if autoFilter is true')
    }
    const state = applyTableStateParams({ config, rows, params })
    return {
      serverParams: params,
      rows: state.rows,
      defaultState: defaultState,
      filteringTier: 'server',
      clientQuickFilters: clientQuickFilters || [],
      rowCount: state.rowCount
    }
  } else if (rows) {
    return {
      serverParams: params,
      defaultState: defaultState,
      rows: rows,
      filteringTier: 'client',
      clientQuickFilters: clientQuickFilters || [],
      rowCount: rows.length
    }
  } else {
    return {
      serverParams: params,
      defaultState: defaultState,
      filteringTier: 'server',
      clientQuickFilters: clientQuickFilters || []
    }
  }
}
